import "../../kendo.toolbar.js"; // Still required until kendo.ui is modularized
import { IKendoJQueryStatic, IKendoJQuery } from "../../core/index.js";
import { Widget } from "../../core/base/widget.js";
import { STYLES, DOT, CLICK } from "../constants.js";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

/**
 * MessageToolbar provides toolbar functionality for chat messages.
 * Handles quick actions that can be performed on messages through toolbar buttons.
 *
 * This is an internal collaborator that extends Widget for event handling.
 */
export class MessageToolbar extends Widget {
    static options = {
        name: "MessageToolbar"
    };

    private toolbar: any;

    /**
     * Constructs a new MessageToolbar instance.
     */
    constructor(element: IKendoJQuery, options: any) {
        super(element, $.extend(true, {}, MessageToolbar.options, options));

        this.extendItemsConfig();
        this.create();
        this.attachEvents();
    }

    /**
     * Creates the underlying Kendo UI ToolBar.
     */
    private create(): void {
        const existingToolBar = this.element.data("kendoToolBar");
        if (existingToolBar) {
            existingToolBar.destroy();
        }

        const toolbarOptions = $.extend({}, this.options, {
            fillMode: "flat"
        });
        delete toolbarOptions.name;

        this.toolbar = new kendo.ui.ToolBar(this.element, toolbarOptions);
    }

    /**
     * Extends items configuration with default properties.
     */
    private extendItemsConfig(): void {
        const items = (this.options as any).items;
        if (items) {
            items.forEach((item: any) => {
                item.attributes = item.attributes || {};

                const ariaLabel = item.attributes["aria-label"];

                item.attributes["data-command"] = item.name.toLowerCase();
                item.attributes["aria-label"] = ariaLabel ?? item.name;
                item.type = "button";
                item.fillMode = "flat";
                item.overflow = "never";
            });
        }
    }

    /**
     * Attaches event handlers to the toolbar.
     */
    private attachEvents(): void {
        this.toolbar.bind(CLICK, this.onClick.bind(this));
    }

    /**
     * Handles toolbar button clicks.
     */
    private onClick(e: any): void {
        const message = e.target.closest(DOT + STYLES.message);
        const command = e.target.data("command");

        if (command) {
            this.executeCommand(command, e.target, message);
        }
    }

    /**
     * Executes a command from the toolbar.
     */
    private executeCommand(command: string, item: IKendoJQuery, message: IKendoJQuery): void {
        this.trigger("execute", { type: command, item, message });
    }

    destroy(): void {
        if (this.toolbar) {
            this.toolbar.destroy();
            this.toolbar = null;
        }

        this.element.empty();
    }
}
